import * as Sentry from "@sentry/browser";
import type { BrowserOptions } from "@sentry/browser";
import type { Envelope } from "@sentry/core";

// Sentry doesn't export `BrowserTransportOptions` from a public entrypoint; recover it
// from the transport factory signature on the public init options.
type BrowserTransportOptions = Parameters<NonNullable<BrowserOptions["transport"]>>[0];

type CloudTransportConfig = {
	// A getter is resolved per-send, for endpoints not known at init time; a plain
	// string is used as-is.
	endpoint: string | (() => string);
	// A getter is resolved per-send, for names that can change after init; a plain
	// string is used as-is.
	appName: string | (() => string);
	appVersion: string;
	// Resolved per send, so tokens established after init are picked up.
	getAuthHeaders?: () => Record<string, string>;
};

type EnvelopeEventItem = {
	exception?: { values?: { value?: string }[] };
};

// An envelope is `[header, items]`, each item a `[itemHeader, payload]` tuple.
// Only envelopes containing an "event" item (errors/messages) are forwarded,
// so non-error telemetry never reaches the endpoint.
const containsEvent = (envelope: Envelope): boolean => {
	const items = envelope[1] as unknown as [{ type?: string }, unknown][];
	return items?.some(item => item?.[0]?.type === "event") ?? false;
};

export const makeCloudTransport =
	({ endpoint, appName, appVersion, getAuthHeaders }: CloudTransportConfig) =>
	(options: BrowserTransportOptions) => {
		const pending: Envelope[] = [];

		const transport = Sentry.createTransport(options, async () => {
			const envelope = pending.shift();
			if (!envelope) return {};

			const items = envelope[1] as unknown as [unknown, EnvelopeEventItem][];
			const message = items?.[0]?.[1]?.exception?.values?.[0]?.value;

			const app = typeof appName === "function" ? appName() : appName;
			const body = JSON.stringify({
				app,
				app_version: appVersion,
				message,
				data: envelope,
			});

			const headers = new Headers(options.fetchOptions?.headers);
			Object.entries(getAuthHeaders?.() ?? {}).forEach(([key, value]) => headers.set(key, value));
			headers.set("Content-Type", "application/json");
			headers.set("wev-app-version", `${app}/${appVersion}`);

			const url = typeof endpoint === "function" ? endpoint() : endpoint;
			await fetch(url, {
				...options.fetchOptions,
				body,
				method: "POST",
				referrerPolicy: "origin",
				headers,
				// Survives page unload; keepalive caps the body at 64KB, so only set
				// it when the payload's byte size is safely under.
				keepalive: new TextEncoder().encode(body).length < 60_000,
			});
			return {};
		});

		const originalSend = transport.send;
		transport.send = async (envelope: Envelope) => {
			// Drop anything that isn't a real error event. Resolve as a successful
			// no-op so the SDK doesn't treat the skip as a transport failure.
			if (!containsEvent(envelope)) {
				return {};
			}
			pending.push(envelope);
			try {
				return await originalSend(envelope);
			} finally {
				// The SDK can settle without invoking the request executor (e.g. full
				// buffer); drop the queued envelope so later sends don't ship a stale one.
				const index = pending.indexOf(envelope);
				if (index > -1) pending.splice(index, 1);
			}
		};

		return transport;
	};
